// src/lib/healthBridge.js
//
// Bridge from the web app to the native ResetHealth plugin (mobile/plugins/
// reset-health). In a browser, `available()` is false and nothing here runs —
// the same bundle serves the web app and the iOS/Android shell.
//
// The plugin is reached through the global Capacitor runtime the shell
// injects (window.Capacitor.Plugins.ResetHealth), so the web bundle carries
// no native dependency.

import { API_BASE } from "../api";

const SYNC_DAYS_FIRST = 120; // first sync: enough history for the baseline
const SYNC_DAYS_OVERLAP = 14; // later syncs: re-read recent days (edits, late writes)
const LAST_SYNC_KEY = "reset.health.lastSync";

function plugin() {
  if (typeof window === "undefined") return null;
  const cap = window.Capacitor;
  if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return null;
  return (cap.Plugins && cap.Plugins.ResetHealth) || null;
}

/** 'healthkit' on iOS, 'healthconnect' on Android, null in a browser. */
export function healthSource() {
  if (!plugin()) return null;
  const p = window.Capacitor.getPlatform();
  if (p === "ios") return "healthkit";
  if (p === "android") return "healthconnect";
  return null;
}

export function healthLabel(source = healthSource()) {
  if (source === "healthkit") return "Apple Health";
  if (source === "healthconnect") return "Health Connect";
  return null;
}

export function available() {
  return healthSource() != null;
}

function readLastSync() {
  try {
    const v = Number(window.localStorage.getItem(LAST_SYNC_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch (_) {
    return null;
  }
}

function writeLastSync(ms) {
  try {
    window.localStorage.setItem(LAST_SYNC_KEY, String(ms));
  } catch (_) {
    /* storage unavailable — next sync re-reads the full window */
  }
}

/**
 * Ask for read access, read workouts since the last sync (with overlap) and
 * post them to the API. Resolves { added, updated, total, skipped }.
 * Rejects with a user-presentable Error.
 */
export async function syncHealth({ full = false } = {}) {
  const p = plugin();
  const source = healthSource();
  if (!p || !source) throw new Error("Health data is only available in the RESET app.");

  const access = await p.requestAccess();
  if (!access || access.granted === false) {
    throw new Error(source === "healthkit"
      ? "No access to Apple Health. Allow it in Settings › Health › Data Access & Devices › RESET."
      : "No access to Health Connect. Allow it in Health Connect › App permissions › RESET.");
  }

  const last = full ? null : readLastSync();
  const days = last ? Math.min(SYNC_DAYS_FIRST, Math.ceil((Date.now() - last) / 864e5) + SYNC_DAYS_OVERLAP) : SYNC_DAYS_FIRST;
  const since = new Date(Date.now() - days * 864e5).toISOString();
  const { workouts = [] } = (await p.readWorkouts({ since })) || {};

  const res = await fetch(`${API_BASE}/api/activities/health`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source, workouts }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error((json && json.error) || `Sync failed (HTTP ${res.status}).`);
  writeLastSync(Date.now());
  return json;
}

/** Upload one .fit / .gpx file. Resolves the server's import summary. */
export async function uploadActivityFile(file) {
  const res = await fetch(`${API_BASE}/api/activities/upload?filename=${encodeURIComponent(file.name || "")}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/octet-stream" },
    body: file,
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error((json && json.error) || `Upload failed (HTTP ${res.status}).`);
  return json;
}
